import * as Headings from '@cian/ui-kit/typography/UI/UIHeading';

import { clsx } from 'clsx';
import { type FC, type JSX, type ReactNode, useMemo } from 'react';

import { useCAForCard } from '@/microfrontends/listing/shared/hooks/card/useCAForCard';
import { splitStringToMaxCharsInRow } from '@/microfrontends/listing/shared/utils/publisher';

import { Link, WrapElementsSpacer } from '@/packages/shared-ui';
import { stopPropagation } from '@/packages/utils/event/stopPropagation';

import { type IPublisherSchema } from '@/repositories/generated/shared/search-offers/entities/publisher/PublisherSchema';

import styles from './LinkToPublisherProfile.css';

type LinkToPublisherProfileProps = {
  badges: Array<ReactNode>;
  publisher: IPublisherSchema;
  as: keyof typeof Headings;
  withTextOverflowEllipsis: boolean;
};

export const LinkToPublisherProfile: FC<LinkToPublisherProfileProps> = props => {
  const { badges, publisher, withTextOverflowEllipsis } = props;

  const { url, name } = publisher;

  const caForCard = useCAForCard();

  const splitPublisherNameViews = useMemo<Array<string | JSX.Element>>(() => {
    const splitPublisherName = splitStringToMaxCharsInRow(name, 20);
    const lastSplitPublisherNameIndex = splitPublisherName.length - 1;

    const result = Array.of<string | JSX.Element>();

    for (let index = 0; index < lastSplitPublisherNameIndex; index += 1) {
      result.push(splitPublisherName[index], <br key={`br_${index}`} />);
    }

    result.push(
      <WrapElementsSpacer horizontalSpace={4} key={lastSplitPublisherNameIndex} verticalSpace={0}>
        {splitPublisherName[lastSplitPublisherNameIndex]}
        <WrapElementsSpacer horizontalSpace={4} verticalSpace={4}>
          {badges}
        </WrapElementsSpacer>
      </WrapElementsSpacer>,
    );

    return result;
  }, [badges, name]);

  const UIHeading = Headings[props.as];

  return (
    <UIHeading>
      {url ? (
        <Link
          behaviour="hover_black_to_primary"
          display="inline-block"
          href={url}
          track={(): void => caForCard({ action: 'click_sopr', category: 'Listing', label: 'to_publisher' })}
          onClick={stopPropagation}
        >
          {splitPublisherNameViews}
        </Link>
      ) : (
        <div className={clsx(withTextOverflowEllipsis && styles['with-overflow'])}>{splitPublisherNameViews}</div>
      )}
    </UIHeading>
  );
};
